import { useEffect, useMemo, useState } from "react";
import { SubmitHandler, UseFormReturn, useForm } from "react-hook-form";
import defaultFormConfig from "../../../helpers/validation/defaultFormConfig";
import ConnectorRegistry from "../controllers/ConnectorRegistry";
import { z } from "zod";
import ConnectorRuleSet from "../models/ConnectorRuleSet";
import FormWizard, { WizardStep } from "../../common/Wizard/FormWizard";
import ConnectorConfigForm, {
  ConnectorFormAction,
} from "./ConnectorConfigForm";
import RuleSetForm from "./RuleSetForm";
import Connector, { IConnectorRow } from "../models/Connector";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { useGetConnector } from "../../../hooks/useConnector";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

const FORM_ID = "connector-wizard";

const getSteps = (
  action: ConnectorFormAction,
  onSubmit: SubmitHandler<ConnectorWizardFormValues>,
  form: UseFormReturn<ConnectorWizardFormValues>,
  t: TFunction,
  connector?: Connector<ConnectorWizardFormValues>,
): WizardStep<ConnectorWizardFormValues>[] => {
  const connectorFields = connector?.fields ?? [];
  return [
    {
      index: 0,
      title: t("connectors:configureSecrets"),
      content: (
        <ConnectorConfigForm
          action={action}
          formId={FORM_ID}
          form={form}
          onSubmit={onSubmit}
        />
      ),
      fieldsToValidate: ["type", "intervalSeconds", ...connectorFields],
    },
    {
      index: 1,
      title: t("connectors:addRuleSets"),
      content: <RuleSetForm formId={FORM_ID} form={form} onSubmit={onSubmit} />,
      fieldsToValidate: ["ruleSets"],
    },
  ];
};

type CommonConnectorConfigFormValues = {
  type: string;
  intervalSeconds: number;
  ruleSets: ConnectorRuleSet[];
  shouldSaveNoMedia: boolean;
};
export type ConnectorWizardFormValues = CommonConnectorConfigFormValues &
  z.infer<typeof controller.schema>;

const controller = ConnectorRegistry.getInstance();

export interface ConnectorWizardProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onSubmit: SubmitHandler<ConnectorWizardFormValues>;
  action: ConnectorFormAction;
  connectorType?: string;
}

const getDefaultValues = (
  action: ConnectorFormAction,
  connectorRow: IConnectorRow | null | undefined,
  connectorType?: string,
) => {
  if (action === "Edit") {
    if (!connectorType) {
      return controller.defaultValues;
    }
    const connector = controller.connectorMap[connectorType];
    if (!connectorRow) return controller.defaultValues;
    return {
      ...controller.defaultValues,
      ...connectorRow,
      shouldSaveNoMedia: connectorRow.shouldSaveNoMedia,
      type: connectorRow.type,
      intervalSeconds: connectorRow.intervalSeconds
        ? connectorRow.intervalSeconds
        : controller.defaultValues.intervalSeconds,
      [connectorType]: connector.getEditDefaultValues(connectorRow),
    };
  }
  return controller.defaultValues;
};

const ConnectorWizard = ({
  isOpen,
  setIsOpen,
  onSubmit,
  action,
  connectorType,
}: ConnectorWizardProps) => {
  const { t } = useTranslation("connectors");
  const [activeStepIndex, setActiveStepIndex] = useState<number>(0);
  const { customerId } = useParams();
  const queryClient = useQueryClient();
  const {
    data: selectedConnector,
    error,
    isLoading: isConnectorLoading,
  } = useGetConnector(customerId, connectorType, action === "Edit" && isOpen);

  useEffect(() => {
    if (isOpen) {
      return;
    }
    queryClient.resetQueries({
      queryKey: ["connector", customerId, connectorType],
    });
  }, [isOpen, queryClient, customerId, connectorType]);

  const defaultValues = useMemo(() => {
    return getDefaultValues(action, selectedConnector, selectedConnector?.type);
  }, [action, selectedConnector]);
  const form = useForm<ConnectorWizardFormValues>({
    ...defaultFormConfig<ConnectorWizardFormValues>(controller.schema),
    defaultValues,
  });
  const { formState, reset, trigger } = form;
  const { isSubmitting, isLoading } = formState;

  useEffect(() => {
    if (action === "Edit" || selectedConnector) {
      reset(defaultValues); // Reset with the selected connector's non-sensitive data
    } else {
      reset(); // Clear form for new connector creation
    }
    setActiveStepIndex(0);
  }, [isOpen, action, reset, selectedConnector, defaultValues]);

  const handleFormSubmit = async () => {
    try {
      await onSubmit(form.getValues());
      reset();
      setActiveStepIndex(0);
    } catch (error) {
      console.error("Submission failed:", error);
    }
  };

  return (
    <FormWizard
      isLoading={isConnectorLoading}
      error={error}
      formId={FORM_ID}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      onClose={reset}
      isBackDisabled={isSubmitting || isLoading}
      isNextDisabled={isSubmitting || isLoading}
      validationTrigger={trigger}
      steps={getSteps(
        action,
        handleFormSubmit,
        form,
        t,
        controller.connectorMap[connectorType ?? ""] as
          | Connector<ConnectorWizardFormValues>
          | undefined,
      )}
      activeStepIndex={activeStepIndex}
      setActiveStepIndex={setActiveStepIndex}
    />
  );
};

export default ConnectorWizard;
